'use no memo';

import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { ProgressCircle } from '@/components/Progress';
import { TooltipWrapper } from '@/components/Tooltip/TooltipWrapper';
import { Text } from '@/components/Typography/Text';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { operationInstanceQueryKeys } from '@/features/operation-instances/hooks';
import { StepCard } from '@/features/operation-package-instance-profile/-components/StepCard';
import { OperationPackageInstanceTargetLink } from '@/features/operation-package-instance/-components/OperationPackageInstanceTargetLink';
import { LabelPerson } from '@/features/person/components/LabelPerson';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { useTaskOperation } from './-hooks/useTaskOperation';
import { useTodoAction } from './-hooks/useTodoAction';
import {
  useQueryOperationPackageInstanceProgress,
  useSuspenseQueryOperationPackageInstance,
  usePostOperationPackageInstanceApprove,
} from './-queries';
import { TaskStatus } from './-types/operation-package';
export const Route = createFileRoute(
  '/_staff/attendance-core/_layout/operation-packages/$id/patterns/$patternId/instances/$instanceId/_profile/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation(defaultNS);
  const { instanceId } = Route.useParams();
  const { data } = useSuspenseQueryOperationPackageInstance(instanceId);
  const { data: progressData } =
    useQueryOperationPackageInstanceProgress(instanceId);
  const { toast } = useToast();
  const todoAction = useTodoAction();
  const queryClient = useQueryClient();
  const handleAdminMutationSuccess = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: operationInstanceQueryKeys.detail('HRF_LABOR_CORE', instanceId),
    });
  }, [queryClient, instanceId]);

  const completedCount = progressData?.completedCount ?? 0;
  const totalCount = progressData?.totalCount ?? 0;

  // 承認待ちタスクがあるかどうかをチェック
  const hasWaitingApprovalTasks = data.operationSteps.some((step) =>
    step.operationTaskInstances.some(
      (task) => task.status === TaskStatus.WaitingApproval,
    ),
  );

  // 承認待ちのタスクIDを取得
  const waitingApprovalTaskIds = data.operationSteps
    .flatMap((step) => step.operationTaskInstances)
    .filter((task) => task.status === TaskStatus.WaitingApproval)
    .map((task) => task.id);

  // インスタンスのステータスによってアクションの可否を判定
  const canApprove =
    data.status === TaskStatus.InProgress && hasWaitingApprovalTasks;

  // 完了処理用
  const { mutate: approveInstance, isPending: isApprovePending } =
    usePostOperationPackageInstanceApprove(instanceId, waitingApprovalTaskIds, {
      onSuccess: () => {
        toast({
          description: t(
            'features.operationPackageInstance.action.approveAllSuccess',
          ),
        });
      },
      onError: (message) => {
        toast({
          variant: 'destructive',
          description: message,
        });
      },
    });

  return (
    <FillScreen>
      <HStack className="px-12 py-7 gap-9 h-full">
        <VStack className="w-full border rounded-lg p-12 gap-3 shadow-xs bg-white overflow-auto">
          <HStack className="justify-between items-center">
            <Text variant="h2">{data.name}</Text>
            <TooltipWrapper
              content={
                !canApprove
                  ? t(
                      'features.operationPackageInstance.action.cannotApproveAll',
                    )
                  : undefined
              }
              side="top"
            >
              <ActionButton
                variant="secondary"
                onClick={() => approveInstance()}
                isLoading={isApprovePending}
                disabled={!canApprove}
              >
                {t('features.operationPackageInstance.action.approveAll')}
              </ActionButton>
            </TooltipWrapper>
          </HStack>
          {data.description && <Text>{data.description}</Text>}
          <HStack className="border-2 rounded-md">
            <OperationPackageInstanceTargetLink target={data.target} />
            <Separator orientation="vertical" />
            <VStack className="p-4 gap-2">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('pages.attendanceCore.instance.assignee')}
              </Text>
              <Link to="/person/$id" params={{ id: data.operator.operatorId }}>
                <LabelPerson
                  name={data.operator.operatorName}
                  avatarUrl={data.operator.operatorAvatarUrl}
                  size="Default"
                  hoverScale={false}
                />
              </Link>
            </VStack>
          </HStack>
          <VStack className="gap-4">
            {data.operationSteps.map((step, index) => (
              <StepCard
                key={step.id}
                step={step}
                stepNumber={index + 1}
                isLastStep={index === data.operationSteps.length - 1}
                nextStep={data.operationSteps[index + 1]}
                isPackageAbort={data.status === 'Aborted'}
                useTaskOperation={useTaskOperation}
                todoAction={todoAction}
                instanceTarget={data.target}
                designationContext={{
                  allSteps: data.operationSteps,
                }}
                enableAdminActions
                onAdminMutationSuccess={handleAdminMutationSuccess}
              />
            ))}
          </VStack>
        </VStack>

        <div className="h-fit hidden lg:block">
          <ProgressCircle
            current={completedCount}
            total={totalCount}
            title={t('pages.attendanceCore.instance.progress.title')}
            expandable={false}
          />
        </div>
      </HStack>
    </FillScreen>
  );
}
